import type { Result as SearchProduct } from "@/generated/api/types.gen"
import { buildRoute } from "@/lib/routes"
import type { SearchQuery } from "@/lib/types/search"

export type SearchOption = { kind: "query"; key: string; label: string; term: string } | { kind: "product"; key: string; label: string; product: SearchProduct; href: string }

const normalizeSearchText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim()
    .toLowerCase()

export const findExactSearchProduct = (products: SearchProduct[], query: string): SearchProduct | undefined => {
  const queryKey = normalizeSearchText(query)
  if (!queryKey) return undefined
  return products.find((product) => Boolean(product.productUrl) && normalizeSearchText(product.name) === queryKey)
}

const isPlainTextQuery = (query: SearchQuery): boolean =>
  query.categories.length === 0 &&
  query.collections.length === 0 &&
  Object.values(query.attributes).every((values) => values.length === 0) &&
  query.priceMin === undefined &&
  query.priceMax === undefined &&
  query.sort === "relevance"

export const exactProductHref = (products: SearchProduct[], text: string): string | undefined => {
  const product = findExactSearchProduct(products, text)
  return product?.productUrl ? buildRoute.productFromUrl(product.productUrl) : undefined
}

export const exactSearchProductHref = (query: SearchQuery, products: SearchProduct[]): string | undefined =>
  isPlainTextQuery(query) ? exactProductHref(products, query.text) : undefined

export const buildSearchOptions = ({ query, suggestions, products, limit }: { query: string; suggestions: string[]; products: SearchProduct[]; limit: number }): SearchOption[] => {
  if (limit <= 0) return []

  const options: SearchOption[] = []
  const queryKey = normalizeSearchText(query)
  const exactProduct = findExactSearchProduct(products, query)
  const productKeys = new Set<string>()

  if (exactProduct?.productUrl) {
    const href = buildRoute.productFromUrl(exactProduct.productUrl)
    const key = `product:${exactProduct.productGroupUuid || href}`
    options.push({ kind: "product", key, label: exactProduct.name, product: exactProduct, href })
    productKeys.add(key)
  }

  const suggestionKeys = new Set<string>(queryKey ? [queryKey] : [])
  for (const suggestion of suggestions) {
    if (options.length >= limit) return options
    const label = suggestion.trim()
    const key = normalizeSearchText(label)
    if (!key || suggestionKeys.has(key)) continue
    suggestionKeys.add(key)
    options.push({ kind: "query", key: `query:${key}`, label, term: label })
  }

  for (const product of products) {
    if (options.length >= limit) break
    if (!product.productUrl || product === exactProduct) continue
    const href = buildRoute.productFromUrl(product.productUrl)
    const key = `product:${product.productGroupUuid || href}`
    if (productKeys.has(key)) continue
    productKeys.add(key)
    options.push({ kind: "product", key, label: product.name, product, href })
  }

  return options
}
